import {
  ApiError,
  ApiErrorInitialState,
} from '@hellodarwin/core/lib/features/entities';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/index';
import ClientApiV2 from '../client-api-v2';

/** The Client's pending work on one application, from the endpoint that produces it. */
export interface ClientApplicationNotificationCounts {
  tasks: number;
  unread: number;
}

interface ClientApplicationNotificationsPayload {
  programs: ({ program_id: string } & ClientApplicationNotificationCounts)[];
}

export interface ClientApplicationNotificationsState {
  status: 'idle' | 'pending';
  error: ApiError;
  byProgram: Record<string, ClientApplicationNotificationCounts>;
}

const initialState: ClientApplicationNotificationsState = {
  status: 'idle',
  error: ApiErrorInitialState,
  byProgram: {},
};

export const fetchApplicationNotifications = createAsyncThunk<
  ClientApplicationNotificationsPayload,
  { api: ClientApiV2 },
  { rejectValue: ApiError }
>(
  'client/fetchApplicationNotifications',
  async ({ api }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<ClientApplicationNotificationsPayload>(
        '/applications/notifications',
      );
      return data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? ApiErrorInitialState);
    }
  },
  {
    // The navigation bar is mounted for the whole session and the applications page
    // fetches on mount, so landing on it would otherwise fetch twice.
    condition: (_arg, { getState }) =>
      (getState() as RootState).clientApplicationNotifications.status !==
      'pending',
  },
);

const clientApplicationNotificationsSlice = createSlice({
  name: 'clientApplicationNotifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchApplicationNotifications.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(
      fetchApplicationNotifications.fulfilled,
      (state, { payload }) => {
        state.status = 'idle';
        state.error = ApiErrorInitialState;
        state.byProgram = (payload?.programs ?? []).reduce<
          Record<string, ClientApplicationNotificationCounts>
        >((acc, program) => {
          acc[program.program_id] = {
            tasks: program.tasks,
            unread: program.unread,
          };
          return acc;
        }, {});
      },
    );
    builder.addCase(
      fetchApplicationNotifications.rejected,
      (state, { payload }) => {
        state.status = 'idle';
        state.error = payload ?? ApiErrorInitialState;
      },
    );
  },
});

export const clientApplicationNotificationsReducer =
  clientApplicationNotificationsSlice.reducer;

export const selectApplicationNotificationsByProgram = (state: RootState) =>
  state.clientApplicationNotifications.byProgram;

/**
 * The navigation badge. Derived rather than stored beside the per-application counts,
 * so it cannot drift from the cards that account for it.
 */
export const selectApplicationNotificationTotal = createSelector(
  selectApplicationNotificationsByProgram,
  (byProgram) =>
    Object.values(byProgram).reduce(
      (total, counts) => total + counts.tasks + counts.unread,
      0,
    ),
);

/** Not a selector: takes the selected map, so it works inside a render loop. */
export const applicationNotificationCountFor = (
  byProgram: Record<string, ClientApplicationNotificationCounts>,
  programID: string,
) => {
  const counts = byProgram[programID];
  if (!counts) return 0;
  return counts.tasks + counts.unread;
};
